/**
 * Generic attribute-bag readers over a `Record<string, unknown> | null` bag (the projected OTLP `attributes` column
 * shape), plus the raw-string JSON narrowing they are built on. Pure-leaf module: no value imports beyond `const/`
 * siblings. Lives in `const/` so pure `utils/` leaves (which may value-import `@federa/const` leaves but NOT a
 * `@federa/utils` sibling, per `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`) can share one body instead of each
 * re-declaring these coercions locally. `tools/` leaves reach it the same way, which is what keeps the compliance
 * rule bodies free of their own private re-spellings.
 */

import { parseJsonSafe } from '@federa/const/json-parse';
import { nonEmptyStringOrNull, isValidString } from '@federa/const/string-guards';
import { numericFieldOrNull } from '@federa/const/number-guards';
import { asPlainObjectOrNull, asRecordOrNull } from '@federa/const/record-guards';

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): JSON text narrowed to a non-null object record; null for malformed
 * text, a scalar payload, or `null`. An ARRAY passes, because the narrowing is `asRecordOrNull`, the array-including
 * guard. The one body behind every "parse this JSON blob into a record or give up" site -- a malformed payload is a
 * legitimate absence at all of them, never a throw.
 *
 * The parse itself is `parseJsonSafe` (`const/json-parse.ts`) with `preserveLargeIntegers` passed explicitly as
 * `false` by default: an OTLP attribute bag carries no 16+-digit record id, so the plain-parse bytes this reader has
 * always produced stay byte-for-byte what its callers read, and the large-integer decision is recorded rather than
 * defaulted. A caller whose payload CAN carry such an id (a tool call's arguments, a record column) passes `true`,
 * which is what those callers spelled as `parseJsonSafe(raw, null, true) as Record<...>` -- a cast that let a scalar
 * payload through as a "record". ABSENT bytes (`null`, a read that found no file) are the same absence, so a caller
 * holding a nullable read passes it straight in instead of spelling its own null-tolerant front.
 */
export function parseJsonRecord(raw: string | null, preserveLargeIntegers = false): Record<string, unknown> | null {
	return raw === null ? null : asRecordOrNull(parseJsonSafe(raw, null, preserveLargeIntegers));
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): {@link parseJsonRecord}'s PLAIN-OBJECT twin -- the same absences,
 * and an ARRAY is one more, because the narrowing is `asPlainObjectOrNull`. For a reader whose blob is a stamp, a
 * settings root or a payload object, where an array is as malformed as a scalar. The large-integer choice is REQUIRED,
 * never defaulted: seven readers composed `asPlainObjectOrNull(parseJsonSafe(...))` by hand until the 2026-09-25 fold,
 * some on the parse's own default (preserve) and some passing `false`, and a default here would silently pick for half.
 */
export function parseJsonPlainObject(raw: string | null, preserveLargeIntegers: boolean): Record<string, unknown> | null {
	return raw === null ? null : asPlainObjectOrNull(parseJsonSafe(raw, null, preserveLargeIntegers));
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic bag reader homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. Finite number for a numeric or numeric-string
 * value, else null.
 */
export function attrNumber(bag: Record<string, unknown> | null | undefined, key: string): number | null {
	return numericFieldOrNull(bag?.[key]);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic bag reader homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. Non-empty string value, else null.
 */
export function attrString(bag: Record<string, unknown> | null | undefined, key: string): string | null {
	return nonEmptyStringOrNull(bag?.[key]);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic bag reader homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. Parses a JSON-string value through
 * {@link parseJsonRecord}; null for absent, empty, malformed or scalar values (a malformed bag entry is a legitimate
 * absence).
 */
export function attrJson(bag: Record<string, unknown> | null | undefined, key: string): Record<string, unknown> | null {
	const raw = bag?.[key];
	if (!isValidString(raw)) return null;
	return parseJsonRecord(raw);
}
